/**
 * imageDownsize — shrink huge DSLR / phone-camera photos in the browser
 * before they're uploaded to the API. Modern DSLR JPEGs run 15-25 MB on
 * the longest edge of 6000+ px; over a 1-2 Mbps Indian uplink each one
 * can take 80-150 seconds to actually reach our origin, easily blowing
 * past Cloudflare's 100-second edge timeout (HTTP 524).
 *
 * Gemini Nano Banana's effective input ceiling is around 4096 px on the
 * longest edge — anything larger is downsampled on Google's side anyway
 * so we lose nothing by trimming up-front, and we save 80-90 % of bytes
 * on the wire. The original aspect ratio is preserved.
 *
 * EXIF orientation is honoured by `createImageBitmap({imageOrientation:
 * "from-image"})`, which is supported on every desktop browser since
 * 2021 and Safari 17 (Sep 2023). On the rare older Safari we fall back
 * to drawing without orientation correction (the AI still produces a
 * usable result — worst case the user re-uploads).
 *
 * HEIC / unknown encodings fall through untouched: the backend already
 * accepts them and the AI pipeline copes. We do not attempt browser-
 * side HEIC decode because Chrome lacks native support as of 2026.
 */

const MAX_LONGEST_EDGE = 4096;   // Gemini sweet-spot ceiling
const MAX_BYTES_BEFORE_RESIZE = 4 * 1024 * 1024; // 4 MB — leave smaller files alone
const JPEG_QUALITY = 0.9;        // 0.9 keeps colour/highlight detail; smaller files than the source

const DOWNSIZEABLE_MIME = new Set([
  "image/jpeg", "image/jpg", "image/pjpeg",
  "image/png",
  "image/webp",
]);

/**
 * Returns a (possibly new) File ready to upload. Falls back to the
 * original `file` on any failure so we never block a user upload.
 */
export async function downsizeForUpload(file) {
  if (!file) return file;
  if (!DOWNSIZEABLE_MIME.has(file.type)) return file;
  // Cheap files don't need resizing — fast-path them.
  if (file.size <= MAX_BYTES_BEFORE_RESIZE) return file;
  // Canvas / ImageBitmap unavailable (very old browser, or non-DOM env)
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Some older browsers reject the options bag — try without.
    try { bitmap = await createImageBitmap(file); }
    catch { return file; }
  }

  const { width, height } = bitmap;
  const longest = Math.max(width, height);
  if (longest <= MAX_LONGEST_EDGE) {
    // Already small enough — don't bother re-encoding (re-encoding always
    // costs SOME quality even at q=0.9).
    try { bitmap.close?.(); } catch (e) { /* ignore */ }
    return file;
  }

  const scale = MAX_LONGEST_EDGE / longest;
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    try { bitmap.close?.(); } catch (e) { /* ignore */ }
    return file;
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, w, h);
  try { bitmap.close?.(); } catch (e) { /* ignore */ }

  // PNG keeps alpha; everything else goes JPEG for max compression.
  const outType = file.type === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, outType, outType === "image/jpeg" ? JPEG_QUALITY : undefined),
  );
  if (!blob) return file;

  // If the resize somehow grew the file (rare, unusual sources), keep
  // the original.
  if (blob.size >= file.size) return file;

  const base = file.name.replace(/\.[^/.]+$/, "");
  const ext = outType === "image/png" ? "png" : "jpg";
  return new File([blob], `${base}.${ext}`, {
    type: outType,
    lastModified: Date.now(),
  });
}
